import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import MaintenancePage from './pages/MaintenancePage';

// Maintenance kill-switch. Default ON; flip OFF by setting
// REACT_APP_MAINTENANCE_MODE=false in Vercel env and redeploying.
const MAINTENANCE_MODE =
  (process.env.REACT_APP_MAINTENANCE_MODE ?? 'true').toLowerCase() !== 'false';

// Pages
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import UserList from './pages/users/UserList';
import UserDetail from './pages/users/UserDetail';
import AgentList from './pages/agents/AgentList';
import AgentDetail from './pages/agents/AgentDetail';
import LeadsList from './pages/agents/LeadsList';
import AppointmentsList from './pages/agents/AppointmentsList';
import EmailsList from './pages/agents/EmailsList';
import RequestList from './pages/requests/RequestList';
import RequestDetail from './pages/requests/RequestDetail';
import FeedbackList from './pages/feedback/FeedbackList';
import FeedbackDetail from './pages/feedback/FeedbackDetail';
import ConversationList from './pages/conversations/ConversationList';
import ConversationDetail from './pages/conversations/ConversationDetail';
import DocsList from './pages/docs/DocsList';
import DocEditor from './pages/docs/DocEditor';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Profile from './pages/Profile';

// Layout
import AdminLayout from './components/layout/AdminLayout';

// Components
import OfflineAlert from './components/OfflineAlert';
import MobileAlert from './components/MobileAlert';

// Loading Screen
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-text-secondary">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAdminAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Auth Route (redirect if logged in)
function AuthRoute({ children }) {
  const { isAuthenticated, isLoading } = useAdminAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Login */}
      <Route
        path="/login"
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />

      {/* Protected Admin Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="users" element={<UserList />} />
        <Route path="users/:id" element={<UserDetail />} />
        <Route path="agents" element={<AgentList />} />
        <Route path="agents/:id" element={<AgentDetail />} />
        <Route path="leads" element={<LeadsList />} />
        <Route path="appointments" element={<AppointmentsList />} />
        <Route path="emails" element={<EmailsList />} />
        <Route path="requests" element={<RequestList />} />
        <Route path="requests/:id" element={<RequestDetail />} />
        <Route path="feedback" element={<FeedbackList />} />
        <Route path="feedback/:id" element={<FeedbackDetail />} />
        <Route path="conversations" element={<ConversationList />} />
        <Route path="conversations/:id" element={<ConversationDetail />} />
        <Route path="docs" element={<DocsList />} />
        <Route path="docs/:slug" element={<DocEditor />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Short-circuit ALL admin routes (signed-in or not) when maintenance mode
  // is on. The router and auth provider are bypassed so no API calls or
  // session probes fire while the studio is offline.
  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return (
    <AdminAuthProvider>
      <Router>
        <OfflineAlert />
        <MobileAlert />
        <AppRoutes />
      </Router>
    </AdminAuthProvider>
  );
}

export default App;
